
import { ProductList } from '@/models/ProductList'
import { Product } from '@/models/Product'
import { ProductType } from '@/models/ProductType'

import axios from 'axios'
import { AbstractService } from './AbstractService'
import { StorageType } from './StorageService'

export class ProductService extends AbstractService {

  private static api = `${process.env.VUE_APP_BASE_URL}/Umbraco/Api/ProductApi/`

  private async listAsync(hidePending: boolean): Promise<ProductList | null> {
    try {
      const response = await axios.get(`${ProductService.api}List?hidepending=${hidePending}`)
      return super.parseObject<ProductList>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'listAsync')
      return null
    }
  }

  private async getAsync(id: number): Promise<Product | null> {
    try {
      const response = await axios.get(`${ProductService.api}Get?productId=${id}`)
      return super.parseObject<Product>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'getAsync')
      return null
    }
  }

  private async deleteAsync(product: Product): Promise<void> {
    try {
      const response = await axios.delete(`${ProductService.api}Delete?productid=${product.id}&uniqueid=${product.uniqueId}`)
    } catch (error) {
      super.handleAxiosErrors(error, 'deleteAsync')
    }
  }

  private async saveAsync(product: Product): Promise<Product | null> {
    try {
      const response = await axios.post(`${ProductService.api}Save`, product)
      return super.parseObject<Product>(response)
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = '/licensees/login/?reason=timeout'
        return null
      }

      super.handleAxiosErrors(error, 'saveAsync')
      return null
    }
  }

  private async scaffoldProductAsync(): Promise<Product | null> {
    try {
      const response = await axios.get(`${ProductService.api}ScaffoldProduct`)
      return super.parseObject<Product>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'scaffoldProductAsync')
      return null
    }
  }

  private async getTypesAsync(): Promise<ProductType[]> {
    try {
      const response = await axios.get(`${ProductService.api}GetTypes`)
      return super.parseList<ProductType>(response)
    } catch (error) {
      super.handleAxiosErrors(error, 'getTypesAsync')
      return []
    }
  }

  async List(hidePending: boolean): Promise<ProductList | null> {
    return await this.listAsync(hidePending)
  }

  async Get(id: number): Promise<Product | null> {
    return await this.getAsync(id)
  }

  async Delete(product: Product): Promise<void> {
    await this.deleteAsync(product)
  }

  async Save(product: Product): Promise<Product | null> {
    return await this.saveAsync(product)
  }

  async ScaffoldProduct(): Promise<Product | null> {
    return await this.getLookupObject(`product_scaffold`, StorageType.Session, this.scaffoldProductAsync)
  }

  async GetTypes(): Promise<ProductType[]> {
    return await this.getLookupData(`product_types`, StorageType.Session, this.getTypesAsync)
  }
}
